import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  DollarSign, Save, Calendar, Building2, Baby, School, Users, FileText, 
  CheckCircle, AlertCircle, Filter, Download, Mail, Send, CreditCard, Wifi, Receipt
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import API from '@/config/api';
// API imported from config;

// Age group display info
const AGE_GROUP_INFO = {
  infants: { label: 'Infant', icon: Baby, color: 'text-pink-500', bg: 'bg-pink-50' },
  toddlers: { label: 'Toddler', icon: Baby, color: 'text-blue-500', bg: 'bg-blue-50' },
  preschool: { label: 'Preschool', icon: School, color: 'text-green-500', bg: 'bg-green-50' },
  school_age: { label: 'School Age', icon: Users, color: 'text-purple-500', bg: 'bg-purple-50' }
};

const BillingWorksheet = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [generatingForChild, setGeneratingForChild] = useState(null);
  const [worksheet, setWorksheet] = useState([]);
  const [feesSettings, setFeesSettings] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  const [filter, setFilter] = useState('all'); // all, state_assisted, private_pay
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchWorksheet();
  }, [dateRange]);

  const fetchWorksheet = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/billing/worksheet?start_date=${dateRange.start}&end_date=${dateRange.end}`,
        getAuthHeaders()
      );
      setWorksheet(response.data.worksheet || []);
      setFeesSettings(response.data.fees_settings);
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load billing worksheet');
    } finally {
      setLoading(false);
    }
  };

  const updateEntry = (childId, field, value) => {
    setWorksheet(prev => prev.map(entry => {
      if (entry.child_id !== childId) return entry;
      
      const updates = { [field]: value };
      
      // When marking as paid, set the paid date if not already set
      if (field === 'tuition_paid' && value === true && !entry.tuition_paid_date) {
        updates.tuition_paid_date = new Date().toISOString();
      }
      if (field === 'tuition_paid' && value === false) {
        updates.tuition_paid_date = null;
      }
      if (field === 'registration_paid' && value === true && !entry.registration_paid_date) {
        updates.registration_paid_date = new Date().toISOString();
      }
      if (field === 'registration_paid' && value === false) {
        updates.registration_paid_date = null;
      }
      
      return { ...entry, ...updates };
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/billing/worksheet`, {
        entries: worksheet,
        period_start: dateRange.start,
        period_end: dateRange.end
      }, getAuthHeaders());
      toast.success('Billing worksheet saved');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save worksheet');
    } finally {
      setSaving(false);
    }
  };

  // Generate Invoices for Parent Portal
  const handleGenerateInvoices = async () => {
    // Get entries with unpaid balances
    const unpaidEntries = filteredWorksheet.filter(entry => {
      const tuitionDue = Math.max(0, (entry.tuition_amount || 0) - (entry.tuition_state_payment || 0));
      const registrationDue = Math.max(0, (entry.registration_fee || 0) - (entry.registration_state_payment || 0));
      return (!entry.tuition_paid && tuitionDue > 0) || (!entry.registration_paid && registrationDue > 0);
    });

    if (unpaidEntries.length === 0) {
      toast.error('No unpaid balances to generate invoices for');
      return;
    }

    setGeneratingInvoices(true);
    try {
      const response = await axios.post(
        `${API}/billing/worksheet/generate-invoices`,
        {
          entries: unpaidEntries,
          period_start: dateRange.start,
          period_end: dateRange.end,
          include_registration: true
        },
        getAuthHeaders()
      );
      
      toast.success(`${response.data.invoices_created} invoices generated for parent portal`);
      
      if (response.data.invoices_skipped > 0) {
        toast.info(`${response.data.invoices_skipped} entries skipped (no parent email)`);
      }
    } catch (error) {
      toast.error('Failed to generate invoices');
    } finally {
      setGeneratingInvoices(false);
    }
  };

  // Generate Invoice for a single child
  const handleGenerateInvoiceForChild = async (entry) => {
    const tuitionDue = Math.max(0, (entry.tuition_amount || 0) - (entry.tuition_state_payment || 0));
    const registrationDue = Math.max(0, (entry.registration_fee || 0) - (entry.registration_state_payment || 0));
    
    if ((entry.tuition_paid || tuitionDue <= 0) && (entry.registration_paid || registrationDue <= 0)) {
      toast.error('No unpaid balance for this child');
      return;
    }

    setGeneratingForChild(entry.child_id);
    try {
      const response = await axios.post(
        `${API}/billing/worksheet/generate-invoices`,
        {
          entries: [entry],
          period_start: dateRange.start,
          period_end: dateRange.end,
          include_registration: true
        },
        getAuthHeaders()
      );
      
      if (response.data.invoices_created > 0) {
        toast.success(`Invoice generated for ${entry.child_name}`);
      } else if (response.data.invoices_skipped > 0) {
        toast.error(`No parent email linked for ${entry.child_name}`);
      } else {
        toast.info('Invoice already exists for this period');
      }
    } catch (error) {
      toast.error('Failed to generate invoice');
    } finally {
      setGeneratingForChild(null);
    }
  };

  // Download PDF
  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/billing/worksheet/pdf`,
        {
          worksheet: filteredWorksheet,
          period_start: dateRange.start,
          period_end: dateRange.end,
          totals: totals,
          center_name: 'CareKids WI'
        },
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Billing_Worksheet_${dateRange.start}_to_${dateRange.end}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Send Payment Reminders
  const handleSendReminders = async (unpaidTuition, unpaidRegistration) => {
    const allUnpaid = [
      ...unpaidTuition.map(e => ({
        child_id: e.child_id,
        child_name: e.child_name,
        parent_id: worksheet.find(w => w.child_id === e.child_id)?.parent_id,
        type: 'tuition',
        amount: Math.max(0, (e.tuition_amount || 0) - (e.tuition_state_payment || 0))
      })),
      ...unpaidRegistration.map(e => ({
        child_id: e.child_id,
        child_name: e.child_name,
        parent_id: worksheet.find(w => w.child_id === e.child_id)?.parent_id,
        type: 'registration',
        amount: Math.max(0, (e.registration_fee || 0) - (e.registration_state_payment || 0))
      }))
    ].filter(item => item.parent_id);

    if (allUnpaid.length === 0) {
      toast.error('No unpaid items with valid parent info');
      return;
    }

    setSendingReminders(true);
    try {
      const response = await axios.post(
        `${API}/billing/send-payment-reminders`,
        { unpaid_items: allUnpaid },
        getAuthHeaders()
      );
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send reminders. Note: Email domain verification may be required.');
    } finally {
      setSendingReminders(false);
    }
  };

  // Filter worksheet
  const filteredWorksheet = worksheet.filter(entry => {
    if (filter === 'state_assisted') return entry.state_assisted;
    if (filter === 'private_pay') return !entry.state_assisted;
    return true;
  });

  // Calculate totals
  const totals = filteredWorksheet.reduce((acc, entry) => {
    acc.tuition += entry.tuition_amount || 0;
    acc.tuitionStatePaid += entry.tuition_state_payment || 0;
    acc.registration += entry.registration_fee || 0;
    acc.registrationStatePaid += entry.registration_state_payment || 0;
    acc.copay += entry.copay_amount || 0;
    acc.tuitionPaid += entry.tuition_paid ? 1 : 0;
    acc.registrationPaidCount += entry.registration_paid ? 1 : 0;
    acc.onlinePayments += entry.online_payment?.total_paid || 0;
    acc.onlinePaymentCount += entry.online_payment?.has_payment ? 1 : 0;
    return acc;
  }, { tuition: 0, tuitionStatePaid: 0, registration: 0, registrationStatePaid: 0, copay: 0, tuitionPaid: 0, registrationPaidCount: 0, onlinePayments: 0, onlinePaymentCount: 0 });

  if (loading) {
    return (
      <Card className="card-clay">
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-clay" data-testid="billing-worksheet">
      <CardHeader className="pb-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Billing Worksheet
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {hasChanges && (
              <Badge variant="outline" className="text-amber-600 border-amber-300">
                Unsaved changes
              </Badge>
            )}
            <Button 
              onClick={handleSave} 
              disabled={saving || !hasChanges}
              data-testid="save-worksheet-btn"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Worksheet
                </>
              )}
            </Button>
            <Button 
              variant="default"
              className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
              onClick={handleGenerateInvoices} 
              disabled={generatingInvoices || filteredWorksheet.length === 0}
              data-testid="generate-invoices-btn"
            >
              {generatingInvoices ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Generate Invoices
                </>
              )}
            </Button>
            <Button 
              variant="outline"
              onClick={handleDownloadPDF} 
              disabled={downloading || filteredWorksheet.length === 0}
              data-testid="download-worksheet-pdf-btn"
            >
              {downloading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent mr-2" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Date Range & Filters */}
        <div className="flex flex-wrap items-end gap-4 p-4 bg-muted/30 rounded-lg">
          <div className="space-y-1">
            <Label className="text-xs">Period Start</Label>
            <Input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-40"
              data-testid="worksheet-start-date"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Period End</Label>
            <Input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-40"
              data-testid="worksheet-end-date"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Filter</Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Children</SelectItem>
                <SelectItem value="state_assisted">State Assisted</SelectItem>
                <SelectItem value="private_pay">Private Pay</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1" />
          <div className="text-sm text-muted-foreground">
            Showing {filteredWorksheet.length} of {worksheet.length} children
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-600 font-medium">Total Tuition</p>
            <p className="text-xl font-bold text-blue-700">${totals.tuition.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-xs text-green-600 font-medium">State Paid (Tuition)</p>
            <p className="text-xl font-bold text-green-700">${totals.tuitionStatePaid.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-xs text-amber-600 font-medium">Registration Fees</p>
            <p className="text-xl font-bold text-amber-700">${totals.registration.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-xs text-purple-600 font-medium">Total Co-Pay Due</p>
            <p className="text-xl font-bold text-purple-700">${totals.copay.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <p className="text-xs text-emerald-600 font-medium">Tuition Paid</p>
            <p className="text-xl font-bold text-emerald-700">{totals.tuitionPaid}/{filteredWorksheet.length}</p>
          </div>
          <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <p className="text-xs text-indigo-600 font-medium flex items-center gap-1">
              <CreditCard className="w-3 h-3" />
              Online Payments
            </p>
            <p className="text-xl font-bold text-indigo-700">${totals.onlinePayments.toFixed(2)}</p>
            <p className="text-[10px] text-indigo-500">{totals.onlinePaymentCount} payments</p>
          </div>
        </div>

        {/* Unpaid Section */}
        {(() => {
          const unpaidTuition = filteredWorksheet.filter(e => !e.tuition_paid && e.tuition_amount > 0);
          const unpaidRegistration = filteredWorksheet.filter(e => !e.registration_paid && e.registration_fee > 0);
          const totalUnpaidTuition = unpaidTuition.reduce((sum, e) => sum + (e.tuition_amount || 0) - (e.tuition_state_payment || 0), 0);
          const totalUnpaidRegistration = unpaidRegistration.reduce((sum, e) => sum + (e.registration_fee || 0) - (e.registration_state_payment || 0), 0);
          
          if (unpaidTuition.length === 0 && unpaidRegistration.length === 0) return null;
          
          return (
            <Card className="border-red-200 bg-red-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-4 h-4" />
                  Unpaid Balances
                  <Badge variant="destructive" className="ml-2">
                    {unpaidTuition.length + unpaidRegistration.length} items
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Unpaid Tuition */}
                {unpaidTuition.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-sm flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-red-500" />
                        Unpaid Tuition ({unpaidTuition.length})
                      </h4>
                      <span className="font-bold text-red-600">${Math.max(0, totalUnpaidTuition).toFixed(2)}</span>
                    </div>
                    <div className="bg-white rounded-lg border divide-y max-h-40 overflow-auto">
                      {unpaidTuition.map(entry => {
                        const ageInfo = AGE_GROUP_INFO[entry.age_group];
                        const parentDue = (entry.tuition_amount || 0) - (entry.tuition_state_payment || 0);
                        return (
                          <div key={`tuition-${entry.child_id}`} className="p-2 flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              {ageInfo && <ageInfo.icon className={`w-3 h-3 ${ageInfo.color}`} />}
                              <span>{entry.child_name}</span>
                              {entry.state_assisted && (
                                <Badge variant="outline" className="text-[9px] py-0 px-1 text-green-600 border-green-300">State</Badge>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-muted-foreground text-xs mr-2">
                                ${entry.tuition_amount?.toFixed(2)} - ${(entry.tuition_state_payment || 0).toFixed(2)} =
                              </span>
                              <span className="font-bold text-red-600">${Math.max(0, parentDue).toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Unpaid Registration */}
                {unpaidRegistration.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-sm flex items-center gap-1">
                        <FileText className="w-4 h-4 text-amber-500" />
                        Unpaid Registration Fees ({unpaidRegistration.length})
                      </h4>
                      <span className="font-bold text-red-600">${Math.max(0, totalUnpaidRegistration).toFixed(2)}</span>
                    </div>
                    <div className="bg-white rounded-lg border divide-y max-h-40 overflow-auto">
                      {unpaidRegistration.map(entry => {
                        const ageInfo = AGE_GROUP_INFO[entry.age_group];
                        const parentDue = (entry.registration_fee || 0) - (entry.registration_state_payment || 0);
                        return (
                          <div key={`reg-${entry.child_id}`} className="p-2 flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              {ageInfo && <ageInfo.icon className={`w-3 h-3 ${ageInfo.color}`} />}
                              <span>{entry.child_name}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-muted-foreground text-xs mr-2">
                                ${entry.registration_fee?.toFixed(2)} - ${(entry.registration_state_payment || 0).toFixed(2)} =
                              </span>
                              <span className="font-bold text-red-600">${Math.max(0, parentDue).toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Total Unpaid */}
                <div className="pt-2 border-t flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">Total Unpaid Balance:</span>
                    <span className="text-xl font-bold text-red-600">
                      ${(Math.max(0, totalUnpaidTuition) + Math.max(0, totalUnpaidRegistration)).toFixed(2)}
                    </span>
                  </div>
                  <Button 
                    variant="destructive"
                    size="sm"
                    onClick={() => handleSendReminders(unpaidTuition, unpaidRegistration)}
                    disabled={sendingReminders}
                    data-testid="send-reminders-btn"
                  >
                    {sendingReminders ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Payment Reminders
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Worksheet Table */}
        <ScrollArea className="h-[500px] border rounded-lg">
          <table className="mobile-cards w-full text-sm">
            <thead className="bg-muted sticky top-0 z-10">
              <tr>
                <th className="text-left p-3 font-semibold">Child</th>
                <th className="text-left p-3 font-semibold">Schedule</th>
                <th className="text-right p-3 font-semibold">Tuition</th>
                <th className="text-right p-3 font-semibold">State Paid</th>
                <th className="text-right p-3 font-semibold">Amount Due</th>
                <th className="text-center p-3 font-semibold">Paid</th>
                <th className="text-left p-3 font-semibold">Paid Date</th>
                <th className="text-center p-3 font-semibold">
                  <div className="flex items-center justify-center gap-1">
                    <CreditCard className="w-3 h-3" />
                    Online
                  </div>
                </th>
                <th className="text-right p-3 font-semibold">Reg. Fee</th>
                <th className="text-center p-3 font-semibold">Reg. Paid</th>
                <th className="text-left p-3 font-semibold">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredWorksheet.map((entry) => {
                const ageInfo = AGE_GROUP_INFO[entry.age_group];
                // Always calculate dynamically based on current state payment value
                const tuitionDue = Math.max(0, (entry.tuition_amount || 0) - (entry.tuition_state_payment || 0));
                const registrationDue = Math.max(0, (entry.registration_fee || 0) - (entry.registration_state_payment || 0));
                
                return (
                  <tr 
                    key={entry.child_id} 
                    className={cn(
                      "hover:bg-muted/30",
                      entry.state_assisted && "bg-green-50/30"
                    )}
                    data-testid={`worksheet-row-${entry.child_id}`}
                  >
                    {/* Child Name & Info */}
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {ageInfo && (
                          <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", ageInfo.bg)}>
                            <ageInfo.icon className={cn("w-4 h-4", ageInfo.color)} />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{entry.child_name}</p>
                            {/* Individual Generate Invoice Button */}
                            {((!entry.tuition_paid && tuitionDue > 0) || (!entry.registration_paid && registrationDue > 0)) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                onClick={() => handleGenerateInvoiceForChild(entry)}
                                disabled={generatingForChild === entry.child_id}
                                title="Generate invoice for parent portal"
                              >
                                {generatingForChild === entry.child_id ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-indigo-600 border-t-transparent" />
                                ) : (
                                  <Receipt className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {entry.state_assisted && (
                              <Badge variant="outline" className="text-[10px] py-0 px-1 text-green-600 border-green-300">
                                <Building2 className="w-3 h-3 mr-0.5" />
                                State
                              </Badge>
                            )}
                            {entry.case_number && (
                              <span className="text-[10px] text-muted-foreground">#{entry.case_number}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Schedule */}
                    <td className="p-3">
                      <Badge variant="outline" className="capitalize text-xs">
                        {entry.tuition_schedule}
                      </Badge>
                    </td>

                    {/* Tuition Amount */}
                    <td className="p-3 text-right font-medium">
                      <div className="flex flex-col items-end">
                        <span>${(entry.tuition_amount || 0).toFixed(2)}</span>
                        {entry.discount && (
                          <span className="text-xs text-purple-600 flex items-center gap-1">
                            <span className="inline-block w-2 h-2 rounded-full bg-purple-500"></span>
                            {entry.discount.reason === 'sibling' ? 'Sibling' :
                             entry.discount.reason === 'employee' ? 'Employee' :
                             entry.discount.reason === 'scholarship' ? 'Scholarship' :
                             entry.discount.reason === 'military' ? 'Military' :
                             'Discount'}
                            {' '}-${entry.discount.calculated_discount?.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* State Payment for Tuition */}
                    <td className="p-3">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-24 h-8 text-right text-sm"
                        value={entry.tuition_state_payment || ''}
                        onChange={(e) => updateEntry(entry.child_id, 'tuition_state_payment', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                    </td>

                    {/* Amount Due */}
                    <td className="p-3 text-right">
                      <span className={cn(
                        "font-bold",
                        tuitionDue > 0 ? "text-red-600" : "text-green-600"
                      )}>
                        ${tuitionDue.toFixed(2)}
                      </span>
                    </td>

                    {/* Tuition Paid Checkbox */}
                    <td className="p-3 text-center">
                      <Checkbox
                        checked={entry.tuition_paid}
                        onCheckedChange={(checked) => updateEntry(entry.child_id, 'tuition_paid', checked)}
                        className="data-[state=checked]:bg-green-500"
                      />
                    </td>

                    {/* Tuition Paid Date */}
                    <td className="p-3 text-left">
                      {entry.tuition_paid && entry.tuition_paid_date ? (
                        <span className="text-xs text-green-600">
                          {new Date(entry.tuition_paid_date).toLocaleDateString()}
                        </span>
                      ) : entry.tuition_paid ? (
                        <span className="text-xs text-muted-foreground italic">Just now</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>

                    {/* Online Payment Indicator */}
                    <td className="p-3 text-center">
                      {entry.online_payment?.has_payment ? (
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            <CreditCard className="w-3 h-3" />
                            ${entry.online_payment.total_paid.toFixed(2)}
                          </div>
                          {entry.online_payment.payments?.[0]?.date && (
                            <span className="text-[10px] text-blue-600">
                              {new Date(entry.online_payment.payments[0].date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>

                    {/* Registration Fee */}
                    <td className="p-3 text-right font-medium text-amber-600">
                      ${(entry.registration_fee || 0).toFixed(2)}
                      {registrationDue > 0 && registrationDue !== entry.registration_fee && (
                        <div className="text-xs text-red-500">Due: ${registrationDue.toFixed(2)}</div>
                      )}
                    </td>

                    {/* Registration Paid Checkbox */}
                    <td className="p-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <Checkbox
                          checked={entry.registration_paid}
                          onCheckedChange={(checked) => updateEntry(entry.child_id, 'registration_paid', checked)}
                          className="data-[state=checked]:bg-green-500"
                        />
                        {entry.registration_paid && entry.registration_paid_date && (
                          <span className="text-[10px] text-green-600">
                            {new Date(entry.registration_paid_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Notes */}
                    <td className="p-3">
                      <Input
                        type="text"
                        className="w-32 h-8 text-sm"
                        value={entry.notes || ''}
                        onChange={(e) => updateEntry(entry.child_id, 'notes', e.target.value)}
                        placeholder="Notes..."
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {filteredWorksheet.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No children found matching the filter</p>
            </div>
          )}
        </ScrollArea>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-100 border border-green-300 rounded" />
            <span>State Assisted</span>
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-green-500" />
            <span>Manually Marked Paid</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] flex items-center gap-0.5">
              <CreditCard className="w-2.5 h-2.5" />
              $XX
            </div>
            <span>Online Payment (Stripe)</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-red-500 font-medium">$X.XX</span>
            <span>= Amount Due</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-green-500 font-medium">$0.00</span>
            <span>= Fully Paid</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BillingWorksheet;
